import geoService from '../../services/GeoService';
import { languaSessionHeader } from '../../services/languaSessionHeader';

// Token refresh timing constants
const TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000; // 10 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;    // 5 minutes
const TOKEN_EXPIRY_THRESHOLD_MS = 60 * 1000;    // 1 minute

export default class WorkerChatConnection {
  constructor(options) {
    this.chatId = options.chatId;
    const form = document.getElementById('chat-form');
    this.dedicatedWorker = form?.dataset?.dedicatedChatWorker === 'true';
    this.pendingAssistant = null;
    this.requestInFlight = false;
    this.userId = options.userId;
    this.userToken = null;  // Will be fetched on demand
    this.tokenExpiresAt = null;  // Track token expiration
    this.railsApiUrl = options.railsApiUrl;
    this.chatOptions = options.chatOptions;
    this.callbacks = options.callbacks;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.tokenRefreshTimer = null;  // Timer for auto-refresh
    this.heartbeatInterval = null;  // Heartbeat interval
    this.tokenRefreshPromise = null; // Shared promise to prevent concurrent refreshes

    // Determine Worker URL based on environment
    this.workerUrl = this.getWorkerUrl();

    // Setup visibility handler for page wake detection
    this.setupVisibilityHandler();
  }

  /**
   * Fetch a short-lived token for Worker authentication
   * Uses a shared promise to prevent concurrent refresh operations
   */
  async fetchWorkerToken() {
    // If a refresh is already in progress, return the existing promise
    if (this.tokenRefreshPromise) {
      console.log('Token refresh already in progress, waiting...');
      return this.tokenRefreshPromise;
    }

    // Create new refresh promise
    this.tokenRefreshPromise = (async () => {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

        const response = await fetch('/langua/worker_tokens', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || ''
          },
          credentials: 'same-origin',
          body: JSON.stringify({ chat_id: this.chatId })
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch worker token: ${response.status}`);
        }

        const data = await response.json();

        // Store token and expiration
        this.userToken = data.token;
        this.tokenExpiresAt = data.expires_at * 1000; // Convert to milliseconds

        // Schedule token refresh before expiration
        this.scheduleTokenRefresh();

        return data.token;
      } catch (error) {
        console.error('Error fetching worker token:', error);
        throw error;
      } finally {
        // Clear the promise after completion (success or failure)
        this.tokenRefreshPromise = null;
      }
    })();

    return this.tokenRefreshPromise;
  }

  async refreshWorkerToken({ notifyWorker = true } = {}) {
    await this.fetchWorkerToken();
    if (notifyWorker && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'refresh_token', token: this.userToken }));
    }
    return this.userToken;
  }

  /**
   * Schedule automatic token refresh before expiration
   */
  scheduleTokenRefresh() {
    // Clear any existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    if (!this.tokenExpiresAt) return;

    // Refresh before expiration using configured buffer
    const refreshTime = this.tokenExpiresAt - Date.now() - (this.dedicatedWorker ? TOKEN_EXPIRY_THRESHOLD_MS : TOKEN_REFRESH_BUFFER_MS);

    if (refreshTime > 0) {
      // Schedule token refresh silently
      this.tokenRefreshTimer = setTimeout(async () => {
        try {
          await this.refreshWorkerToken();
        } catch (error) {
          console.error('Failed to refresh token:', error);
          // Reconnect with new token if refresh fails
          this.reconnectWithNewToken();
        }
      }, refreshTime);
    }
  }

  /**
   * Check if token needs refresh
   */
  isTokenExpired() {
    if (!this.tokenExpiresAt) return true;
    // Consider expired if less than 1 minute remaining
    return Date.now() > (this.tokenExpiresAt - 60000);
  }

  /**
   * Setup visibility handler to check token when page becomes visible
   * This handles the "laptop closed for 59 minutes" scenario
   */
  setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('Page visible, checking token validity...');

        // Check if token expired while away
        if (this.isTokenExpired()) {
          console.log('Token expired while page was hidden, refreshing...');
          this.refreshWorkerToken()
            .catch(error => {
              console.error('Failed to refresh on visibility:', error);
              this.reconnectWithNewToken();
            });
        }
      }
    });
  }

  /**
   * Start heartbeat to keep connection alive and detect token expiration
   */
  startHeartbeat() {
    // Clear any existing heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Send heartbeat at configured interval
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Check token before sending heartbeat
        if (this.isTokenExpired()) {
          console.log('Token expired during heartbeat, refreshing...');
          (this.dedicatedWorker ? this.refreshWorkerToken() : this.fetchWorkerToken())
            .then(() => {
              if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({ type: 'ping' }));
              }
            })
            .catch(error => {
              console.error('Failed to refresh during heartbeat:', error);
              this.reconnectWithNewToken();
            });
        } else {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        }
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Reconnect with a fresh token
   */
  async reconnectWithNewToken() {
    console.log('Reconnecting with new token...');

    // A token refresh replaces the socket; its close is not a lost reply.
    if (this.ws) {
      if (this.dedicatedWorker) {
        this.ws.tenantIntentionalClose = true;
        if (this.requestInFlight) this.reportError('The response was interrupted. Please try again.');
      }
      this.ws.close();
    }

    // Clear token to force refresh
    this.userToken = null;
    this.tokenExpiresAt = null;

    // Reconnect
    await this.connect();
  }

  getWorkerUrl() {
    // Explicit override, set from ENV['CHAT_WORKER_WS_URL'] by the chat view.
    // Unset everywhere by default, so the host mapping below is unchanged; it
    // exists so a local `wrangler dev` worker (ws://localhost:8787) can be
    // pointed at without editing this file.
    const data = document.getElementById('chat-form')?.dataset;
    const configuredUrl = data?.workerUrl;
    if (this.dedicatedWorker) {
      const allowedWorkerUrls = JSON.parse(data?.workerAllowedUrls || '[]');
      const allowedRailsOrigins = JSON.parse(data?.workerRailsOrigins || '[]');
      if (!allowedWorkerUrls.includes(configuredUrl) || !allowedRailsOrigins.includes(this.railsApiUrl)) {
        throw new Error('Cartus chat endpoints are not configured');
      }
      return configuredUrl;
    }
    if (configuredUrl) {
      return configuredUrl;
    }

    // Check for development environment
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'wss://chat-worker-dev.languatalk.com';
    }

    // Check for ngrok URLs (development)
    if (window.location.hostname.includes('ngrok.app') || window.location.hostname.includes('ngrok-free.app')) {
      return 'wss://chat-worker-dev.languatalk.com';
    }

    if (window.location.hostname.includes('dondev')) {
      return 'wss://chat-worker-dev.languatalk.com';
    }

    // Check for staging
    if (window.location.hostname.includes('staging')) {
      return 'wss://staging-chat.languatalk.com';
    }

    // Production
    return 'wss://chat.languatalk.com';
  }

  async connect() {

    try {
      // Run geo check and token fetch in parallel (they're independent)
      await Promise.all([
        this.dedicatedWorker ? Promise.resolve() : geoService.checkGeoStatus(this.workerUrl),
        this.userToken && !this.isTokenExpired() ? Promise.resolve() : this.fetchWorkerToken()
      ]);
      
      // Get the appropriate worker URL (direct or proxy based on geo)
      const effectiveWorkerUrl = this.dedicatedWorker ? this.workerUrl : geoService.getWorkerUrl(this.workerUrl);

      this.ws = new WebSocket(`${effectiveWorkerUrl}/chat/${this.chatId}`);
      const socket = this.ws;

      this.ws.onopen = () => {
        console.log('Connected to Worker WebSocket');
        this.reconnectAttempts = 0;

        // Send authentication message
        this.authenticate();

        // Start heartbeat to maintain connection and detect token expiration
        this.startHeartbeat();

        if (this.callbacks.connected) {
          this.callbacks.connected();
        }
      };

      this.ws.onmessage = (event) => {
        if (this.dedicatedWorker && (socket !== this.ws || socket.tenantIntentionalClose)) return;
        try {
          const data = JSON.parse(event.data);

          if (this.dedicatedWorker && ['ready', 'pong'].includes(data.type)) return;
          if (this.dedicatedWorker && data.type === 'token_refreshed') {
            if (this.callbacks.contextReady) this.callbacks.contextReady();
            return;
          }

          if (data.type === 'context_initialized' || (this.dedicatedWorker && data.type === 'context_refreshed')) {
            // Context is ready, chat can begin

            // Update chatOptions to reflect Worker's actual settings
            if (data.settings && this.chatOptions) {
              this.chatOptions.clientProvider = data.settings.clientProvider;
              this.chatOptions.model = data.settings.model;
            }

            if (this.callbacks.contextReady) {
              this.callbacks.contextReady();
            }
          } else if (data.type === 'token_expired') {
            console.log('Token expired (detected by worker), refreshing...');

            if (this.dedicatedWorker && this.callbacks.disconnected) this.callbacks.disconnected();
            if (this.dedicatedWorker && this.requestInFlight) this.reportError('Your session is refreshing. Please retry the message.');

            // Immediately try to refresh
            this.refreshWorkerToken()
              .catch(error => {
                console.error('Failed to refresh after expiration:', error);
                // Reconnect with new token as fallback
                this.reconnectWithNewToken();
              });
          } else if (data.type === 'geo_redirect') {
            if (this.dedicatedWorker) {
              this.reportError('Cartus chat cannot use an alternate endpoint.');
              return;
            }
            console.log('Geo-blocking detected, redirecting to proxy:', data.url);

            // Update GeoService to reflect blocked status
            geoService.geoStatus = { 
              country: data.country || 'unknown', 
              blocked: true 
            };

            // Close current connection
            if (this.ws) {
              this.ws.close();
            }

            // Show user feedback if callback exists
            if (this.callbacks.received) {
              this.callbacks.received({
                type: 'info',
                message: data.message || 'Geographic restriction detected. Reconnecting...'
              });
            }

            // Reconnect after brief delay (will automatically use proxy URL via GeoService)
            setTimeout(() => {
              console.log('Reconnecting after geo-redirect...');
              this.connect();
            }, 1000);
          } else if (data.type === 'error') {
            console.error('Worker error:', data.message);
            if (this.dedicatedWorker) this.reportError('The response was interrupted. Please try again.', data.error_type || data.code);
            if (data.code === 'auth_failed' || data.code === 'token_expired') {
              // Try to refresh token and reconnect
              this.reconnectWithNewToken();
            }
          } else if (this.callbacks.received) {
            // Handle different message types from Worker
            if (data.type === 'user_chat_message') {
              // Handle user message confirmation from Worker
              // Check if it has the new structure without HTML
              if (data.message && data.message.full_message) {
                // New format - raw data without HTML
                const userMessageHtml = this.formatUserMessage(data.message.full_message, data.message.chat_message_id);

                // Pass user message through callbacks for proper processing (including auto-correction)
                const userMessage = {
                  type: 'user_chat_message',
                  message: {
                    chat_message_id: data.message.chat_message_id,
                    full_message: data.message.full_message,
                    id: data.message.chat_message_id,
                    data: userMessageHtml,
                    role: 'user'
                  },
                  // Include settings if present
                  settings: data.settings
                };
                this.callbacks.received(userMessage);

                // Show audio playback button if audio exists in localStorage
                this.showAudioButtonIfAvailable(data.message.chat_message_id);
              } else if (data.message && data.chat_message_id) {
                // Legacy format if any
                const userMessageHtml = this.formatUserMessage(data.message, data.chat_message_id);

                const userMessage = {
                  type: 'user_chat_message',
                  message: {
                    chat_message_id: data.chat_message_id,
                    full_message: data.message,
                    id: data.chat_message_id,
                    data: userMessageHtml,
                    role: 'user'
                  }
                };
                this.callbacks.received(userMessage);

                // Show audio playback button if audio exists in localStorage
                this.showAudioButtonIfAvailable(data.chat_message_id);
              } else {
                console.error('User message format not recognized:', data);
              }
            } else if (data.type === 'assistant_message') {
              if (this.dedicatedWorker && data.streaming === true) {
                this.requestInFlight = true;
                this.pendingAssistant = data;
                return;
              }
              this.pendingAssistant = null;
              if (this.dedicatedWorker && data.streaming === false) this.requestInFlight = false;
              // Worker sends assistant_message for text-only mode
              // Transform to ActionCable format for chat_service.js default handler
              // Create properly formatted HTML that matches Rails partial structure
              this.replyAudio = data.settings?.replyAudio === true;
              const messageHtml = this.formatAssistantMessage(data.content, data.messageId);

              const aiMessage = {
                message: {
                  chat_message_id: data.messageId,
                  full_message: data.content,
                  id: data.messageId,
                  data: messageHtml
                },
                // Only a completed dedicated reply may trigger Cartus autoplay.
                ...(this.dedicatedWorker && data.streaming === false ? { cartus_completed_reply: true, cartus_reply_audio: data.settings?.replyAudio === true } : {}),
                // Include settings if present
                settings: data.settings
              };
              this.callbacks.received(aiMessage);
            } else if (data.type === 'audio' && data.audio) {
              if (this.dedicatedWorker && this.completedAudioMessages?.has(data.chat_message_id)) return;
              // Audio chunks are passed as-is
              this.callbacks.received(data);
            } else if (data.type === 'final') {
              if (this.dedicatedWorker) {
                if (!this.completedAudioMessages) this.completedAudioMessages = new Set();
                if (this.completedAudioMessages.has(data.chat_message_id)) return;
                this.completedAudioMessages.add(data.chat_message_id);
                if (this.completedAudioMessages.size > 100) this.completedAudioMessages.delete(this.completedAudioMessages.values().next().value);
              }
              // Final message to mark completion
              this.callbacks.received(data);

              // For voice-enabled messages, we need to also send the AI message
              // with full text after audio is complete
              // Store reference to generate AI message after receiving full text
              if (data.full_text) {
                // Send AI message in ActionCable format for display
                const messageHtml = this.formatAssistantMessage(data.full_text, data.chat_message_id);

                const aiMessage = {
                  message: {
                    chat_message_id: data.chat_message_id,
                    full_message: data.full_text,
                    id: data.chat_message_id,
                    data: messageHtml
                  }
                };
                this.callbacks.received(aiMessage);
              }
            } else if (data.message && data.message.chat_message_id) {
              // Handle messages that come with raw data (no HTML)
              // Determine if it's a user or assistant message based on the structure
              const messageContent = data.message.full_message;
              const messageId = data.message.chat_message_id;

              // Check if this is a user message (could check for role or other indicators)
              // For now, we'll format as assistant message since that's the most common case
              const messageHtml = this.formatAssistantMessage(messageContent, messageId);

              const formattedMessage = {
                chat_id: data.chat_id,
                message: {
                  ...data.message,
                  data: messageHtml
                }
              };

              this.callbacks.received(formattedMessage);
            } else {
              // Pass other message types directly
              this.callbacks.received(data);
            }
          }
        } catch (error) {
          console.error('Error parsing Worker message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('Worker WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('Worker WebSocket closed');
        if (this.dedicatedWorker && (socket !== this.ws || socket.tenantIntentionalClose)) return;
        if (this.dedicatedWorker && this.requestInFlight) this.reportError('The connection was interrupted. Please reconnect and try again.');

        // Clear heartbeat
        if (this.heartbeatInterval) {
          clearInterval(this.heartbeatInterval);
          this.heartbeatInterval = null;
        }

        // Clear refresh timer on disconnect
        if (this.tokenRefreshTimer) {
          clearTimeout(this.tokenRefreshTimer);
          this.tokenRefreshTimer = null;
        }

        if (this.callbacks.disconnected) {
          this.callbacks.disconnected();
        }

        // Attempt to reconnect
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.fallbackToActionCable();
    }
  }

  extractChatConfig() {
    const config = {};

    if (this.chatOptions) {
      // Model settings (matching context endpoint format)
      config.clientProvider = this.chatOptions.clientProvider;
      config.model = this.chatOptions.model;

      // Voice settings (matching context endpoint format)
      config.voice = this.chatOptions.voice;
      config.voiceEnabled = this.chatOptions.voiceEnabled;
      config.voiceProvider = this.chatOptions.voiceProvider;
      config.ttsModel = this.chatOptions.ttsModel;
      config.voiceSpeed = this.chatOptions.speedMultiplier;
      // This client plays each reply at the worker's playbackRate, so the
      // worker may use Eleven v4, which has no speed setting (LAN-1176).
      config.ttsPlaybackRate = true;
      config.voiceGender = this.chatOptions.voiceGender;
      config.voiceModel = this.chatOptions.voiceModel;
      config.language = this.chatOptions.language;
      config.nativeLanguage = this.chatOptions.targetLanguage;
      config.languageCode = this.chatOptions.languageCode;
      config.dialectCode = this.chatOptions.dialectCode;
      config.userLanguageLevel = this.chatOptions.streamLanguageLevel;
      config.pronunciationCharacters = this.chatOptions.pronunciationCharacters;
      config.autoCorrect = this.chatOptions.autoCorrect;
      config.repeatCorrections = this.chatOptions.repeatCorrections;
      config.verbalCorrectionMode = this.chatOptions.verbalCorrectionMode;
    }

    return config;
  }

  authenticate() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Extract chat configuration from chatOptions state (includes model and voice settings)
      const chatConfig = this.extractChatConfig();

      // Send init_context message with chat configuration
      this.ws.send(JSON.stringify({
        type: 'init_context',
        chatId: this.chatId,
        userId: this.userId,
        userToken: this.userToken,
        railsApiUrl: this.railsApiUrl,
        context: {
          // Include chat configuration for Worker (model + voice settings)
          ...chatConfig,
          messages: []
        }
      }));
    }
  }

  joinChat() {
    // No longer needed - init_context handles everything
  }

  refreshContext() {
    // The Worker keeps its own in-memory context. Re-send init_context after
    // settings changes so it re-fetches Rails context/system prompt without a
    // full page reload.
    this.authenticate();
  }

  async checkMessageLimit() {
    try {
      // Get CSRF token for the request
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      // Use relative URL to avoid CORS issues
      const response = await fetch(`/api/v1/chats/${this.chatId}/check_message_limit`, {
        method: 'GET',
        headers: {
          // Declare the client; api/v1 is shared with mobile (LAN-424).
          'X-Langua-Client': 'web',
          'X-Langua-Platform': 'web',
          ...languaSessionHeader(),
          'Authorization': `Bearer ${this.userToken}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        credentials: 'same-origin'
      });

      const data = await response.json();

      // Handle both successful responses (200) and rate limit responses (429)
      if (response.ok || response.status === 429) {
        return data;
      } else {
        console.error('Failed to check message limit:', response.status);
        // On error, allow message to proceed (fail open)
        return { can_send: true };
      }
    } catch (error) {
      console.error('Error checking message limit:', error);
      // On error, allow message to proceed (fail open)
      return { can_send: true };
    }
  }

  async sendMessage(message) {
    // Only check limits for user messages (check for content field, not query)
    if (!this.dedicatedWorker && message.content && message.role === 'user') {
      const limitCheck = await this.checkMessageLimit();

      if (limitCheck.limit_reached) {
        // Send error through callbacks to trigger UI modal
        if (this.callbacks.received) {
          this.callbacks.received({
            error: limitCheck.error || 'Message limit reached',
            error_type: 'message_limit_reached',
            data: limitCheck.data  // Pass the modal HTML
          });
        }
        return; // Don't send the message
      }
    }
    // Check if token is still valid before sending
    if (this.isTokenExpired() && this.dedicatedWorker) {
      try {
        // The fresh token travels atomically with the message; a separate
        // asynchronous refresh frame must not race the message at the Worker.
        await this.refreshWorkerToken({ notifyWorker: false });
        this.sendMessageInternal(message);
      } catch (error) {
        console.error('Failed to refresh token for message:', error);
        this.reportError('Your session could not be refreshed. Please sign in again.');
      }
    } else if (this.isTokenExpired()) {
      this.fetchWorkerToken().then(() => {
        this.sendMessageInternal(message);
      }).catch(error => {
        console.error('Failed to refresh token for message:', error);
      });
    } else {
      this.sendMessageInternal(message);
    }
  }

  sendMessageInternal(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Extract current chat configuration from chatOptions state (includes model and voice settings)
      const chatConfig = this.extractChatConfig();

      // Include chat config with each message so Worker uses updated values
      if (this.dedicatedWorker) this.requestInFlight = true;
      this.ws.send(JSON.stringify({
        type: 'message',
        chatId: this.chatId,
        ...message,
        ...(this.dedicatedWorker ? { userToken: this.userToken } : {}),
        // Include current chat settings (model + voice) so Worker uses updated values
        chatConfig: chatConfig
      }));
    } else {
      console.error('WebSocket is not open. Message not sent.');
      if (this.dedicatedWorker) this.reportError('The message was not sent. Please reconnect and try again.');
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached. Falling back to ActionCable.');
      this.fallbackToActionCable();
    }
  }

  reportError(message, code = 'worker_error') {
    this.pendingAssistant = null;
    this.requestInFlight = false;
    if (this.callbacks.received) {
      this.callbacks.received({ type: 'error', error: true, error_type: code, message, worker_error: true });
    }
  }

  fallbackToActionCable() {
    if (this.dedicatedWorker) {
      this.reportError('Cartus chat is unavailable. Please reload and try again.');
      return;
    }

    // Clear refresh timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    // Update the form to disable Worker mode
    const chatForm = document.getElementById('chat-form');
    if (chatForm) {
      chatForm.dataset.workerEnabled = 'false';
    }

    // Remove the chat_backend=worker parameter and reload
    const url = new URL(window.location);
    url.searchParams.delete('chat_backend');
    window.location.href = url.toString();
  }

  formatUserMessage(content, messageId) {
    // Create HTML structure that matches Rails _user_chat_message.html.erb partial
    // This ensures proper dataset attributes are available for event handlers

    // Escape content for HTML attributes
    const escapeHtml = (str) => {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };

    // Escape content for HTML attributes - handle newlines and quotes
    // Matches Rails template escaping: h(text.gsub('"', '\"').gsub("'", "&#39;"))
    const escapeAttribute = (str) => {
      return escapeHtml(str)
        .replace(/\n/g, '&#10;')  // Escape newlines for attribute values
        .replace(/\r/g, '&#13;')  // Escape carriage returns
        .replace(/"/g, '&quot;')  // Escape double quotes for attributes
        .replace(/'/g, '&#39;');  // Escape single quotes for consistency with Rails
    };

    const escapedContent = escapeAttribute(content);

    // Process the content to add word spans
    const processedContent = content.split(' ').map(word =>
      `          <span class="js-translatable-text js-audio-word word">
            ${escapeHtml(word)}
          </span>`
    ).join('\n');

    // Get data from chat form data attributes
    const chatForm = document.getElementById('chat-form');
    const language = chatForm.dataset.language;
    const userAvatarPath = chatForm.dataset.userAvatarPath;

    return `<div class="chat-message self word-container" id="chat-message-${messageId}" data-chat-message-id="${messageId}">
  <div class="chat-message-content-w">
    <div class="chat-message-content" data-translated-text="${escapedContent}" data-target-language="${language}" data-chat-message-id="${messageId}">
      <div class="chat-message-detail" data-translated-text="${escapedContent}" data-target-language="${language}">
${processedContent}
      </div>
    </div>
  </div>

  <div class="chat-message-correction">
${this.dedicatedWorker ? `      ${chatForm.dataset.correctionsEnabled === 'true' && /^[0-9a-f-]{36}$/i.test(messageId || '') ? `<button type="button" class="btn btn-secondary btn-chat-secondary js-chat-correction-btn" data-chat-message-id="${messageId}" aria-label="Check written correction"><i class="fa fa-pencil" aria-hidden="true"></i></button>` : ''}
` : `      <button type="button" class="btn btn-secondary btn-chat-secondary js-chat-correction-btn" data-text="${escapedContent}" data-language="${language}">
        <i class="fa fa-pencil" aria-hidden="true"></i>
      </button>
`}      <button type="button" class="btn btn-secondary btn-chat-secondary js-alternative-response-btn" data-text="${escapedContent}" data-language="${language}">
        <i class="fa fa-magic" aria-hidden="true"></i>
      </button>
      <a class="btn btn-chat-secondary js-user-chat-translation-btn" data-text="${escapedContent}" data-language="${language}" data-chat-message-id="${messageId}" href="javascript:void(0)">
        <i class="fa fa-language" aria-hidden="true"></i>
      </a>
      <a class="btn btn-chat-secondary js-user-chat-listen-yourself-btn d-none" data-chat-message-id="${messageId}" href="javascript:void(0)">
        <i class="fa fa-volume-up" aria-hidden="true"></i>
      </a>
  </div>

</div>`;
  }

  formatAssistantMessage(content, messageId) {
    // Create HTML structure that matches Rails _chat_message.html.erb partial
    // This ensures proper dataset attributes are available for event handlers

    // Escape content for HTML attributes
    const escapeHtml = (str) => {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    };

    // Escape content for HTML attributes - handle newlines and quotes
    // Matches Rails template escaping: h(text.gsub('"', '\"').gsub("'", "&#39;"))
    const escapeAttribute = (str) => {
      return escapeHtml(str)
        .replace(/\n/g, '&#10;')  // Escape newlines for attribute values
        .replace(/\r/g, '&#13;')  // Escape carriage returns
        .replace(/"/g, '&quot;')  // Escape double quotes for attributes
        .replace(/'/g, '&#39;');  // Escape single quotes for consistency with Rails
    };

    // Strip SSML break tags from content before using in data attributes
    const displayContent = content.replace(/<break\s+time="[^"]*"\s*\/>/g, '');
    const escapedContent = escapeAttribute(displayContent);

    // Check if mode is audio_only to add challenge classes
    const isAudioOnly = this.chatOptions && this.chatOptions.mode === 'audio_only';
    const challengeClass = isAudioOnly ? 'challenge' : '';
    const chatChallengeClass = isAudioOnly ? 'chat-challenge' : '';

    // Check if audio is enabled (not text_only mode)
    const audioEnabled = !this.dedicatedWorker && this.chatOptions && this.chatOptions.mode !== 'text_only';
    const messageSpeechEnabled = this.dedicatedWorker && document.getElementById('chat-form')?.dataset.messageSpeechEnabled === 'true' && /^[0-9a-f-]{36}$/i.test(messageId || '');

    // Process the content to add word spans with proper formatting
    // Also handle bold text between asterisks like Rails does
    const processedContent = displayContent.split('\n').map(line => {
      // Replace *text* with <b>text</b> and **text** with <b>text</b>
      let processedLine = line.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
      processedLine = processedLine.replace(/\*([^*]+)\*/g, '<b>$1</b>');

      // Process the line to wrap words in spans while preserving bold tags
      let result = '';
      let currentPos = 0;

      // Find all bold sections
      const boldRegex = /<b>(.*?)<\/b>/g;
      let match;

      while ((match = boldRegex.exec(processedLine)) !== null) {
        // Process text before the bold section
        const textBefore = processedLine.substring(currentPos, match.index);
        if (textBefore) {
          textBefore.split(' ').forEach(word => {
            if (word.trim()) {
              result += `            <span class="js-translatable-text js-audio-word word ${challengeClass}">
              ${escapeHtml(word)}
            </span>`;
            }
          });
        }

        // Add the entire bold section as one span
        result += `            <span class="js-translatable-text js-audio-word word ${challengeClass}">
              <b>${escapeHtml(match[1])}</b>
            </span>`;

        currentPos = match.index + match[0].length;
      }

      // Process any remaining text after the last bold section
      const remainingText = processedLine.substring(currentPos);
      if (remainingText) {
        remainingText.split(' ').forEach(word => {
          if (word.trim()) {
            result += `            <span class="js-translatable-text js-audio-word word ${challengeClass}">
              ${escapeHtml(word)}
            </span>`;
          }
        });
      }

      return result;
    }).join('<br>\n');

    // Get data from chat form data attributes
    const chatForm = document.getElementById('chat-form');
    const avatarUrl = chatForm.dataset.assistantAvatarPath;
    // Cartus uses the bundled catalog portraits; absent/stale markup gets
    // inert initials instead of fetching a consumer origin.
    const localAvatar = /^\/cartus\/voice-avatars\/[0-9a-f]{64}\.webp$/.test(avatarUrl || '');
    const avatarMarkup = this.dedicatedWorker && !localAvatar
      ? '<span class="rounded-circle bg-secondary text-white p-2" role="img" aria-label="Assistant">AI</span>'
      : this.dedicatedWorker
        ? `<img class="img-fluid rounded-circle" src="${escapeAttribute(avatarUrl || '')}" alt="Assistant" />`
        : `<img class="img-fluid rounded-circle" src="${avatarUrl}" />`;
    const language = chatForm.dataset.language;
    const targetLanguage = chatForm.dataset.targetLanguage;

    return `<div class="chat-message word-container" id="chat-message-${messageId}" data-chat-message-id="${messageId}">
  <div class="chat-message-avatar">
    ${avatarMarkup}
  </div>

  <div class="chat-message-content-w">
    <div class="chat-message-content" data-translated-text="${escapedContent}" data-target-language="${targetLanguage || ''}" data-correction="" data-chat-message-id="${messageId}">

      <div class="chat-message-detail ${chatChallengeClass}" data-translated-text="${escapedContent}">
${processedContent}
      </div>

    </div>
  </div>

    <div class="chat-message-audio">
${messageSpeechEnabled ? `
        <button type="button" class="btn btn-chat-secondary js-cartus-message-speech" data-chat-message-id="${messageId}" aria-label="Play reply" aria-pressed="false"><i class="fa fa-volume-up" aria-hidden="true"></i></button>
        <button type="button" class="btn btn-chat-secondary js-cartus-message-speech" data-chat-message-id="${messageId}" data-playback-rate="0.75" aria-label="Play reply slowly" aria-pressed="false"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" class="turtle-icon"><!--!Font Awesome Pro 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path fill="#ffffff" d="M336 240V208c0-70.7-57.3-128-128-128S80 137.3 80 208v32H336zm48-32v41.2c0 21.4-17.4 38.8-38.8 38.8H70.8C49.4 288 32 270.6 32 249.2V208c0-97.2 78.8-176 176-176s176 78.8 176 176zm96 80c-8.3 0-16.3-1.6-23.6-4.5C440.3 328 400.4 361 352 367v1 72c0 22.1-17.9 40-40 40H264c-22.1 0-40-17.9-40-40V368H192v72c0 22.1-17.9 40-40 40H104c-22.1 0-40-17.9-40-40V368H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H64h48 32 48 32 48 32 32c44.2 0 80-35.8 80-80V224 192 176c0-35.3 28.7-64 64-64c53 0 96 43 96 96v16c0 35.3-28.7 64-64 64H480zM272 368v64h32V368H272zm-160 0v64h32V368H112zM496 224a16 16 0 1 0 0-32 16 16 0 1 0 0 32z"/></svg></button>
      ` : ''}      ${audioEnabled ? `
        <a class="btn btn-chat-secondary js-play-chat-audio-btn" data-text="${escapedContent}" data-language="${language}" data-word-timings="null" data-audio-url="" data-chat-message-id="${messageId}" href="javascript:void(0)">
          <i class="fa fa-volume-up" aria-hidden="true"></i>
        </a>
        <a class="btn btn-chat-secondary js-slow-audio-btn" data-text="${escapedContent}" data-language="${language}" data-normal-speed-audio-url="" data-audio-url="" data-word-timings="" data-chat-message-id="${messageId}" href="javascript:void(0)">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" class="turtle-icon"><!--!Font Awesome Pro 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2024 Fonticons, Inc.--><path fill="#ffffff" d="M336 240V208c0-70.7-57.3-128-128-128S80 137.3 80 208v32H336zm48-32v41.2c0 21.4-17.4 38.8-38.8 38.8H70.8C49.4 288 32 270.6 32 249.2V208c0-97.2 78.8-176 176-176s176 78.8 176 176zm96 80c-8.3 0-16.3-1.6-23.6-4.5C440.3 328 400.4 361 352 367v1 72c0 22.1-17.9 40-40 40H264c-22.1 0-40-17.9-40-40V368H192v72c0 22.1-17.9 40-40 40H104c-22.1 0-40-17.9-40-40V368H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H64h48 32 48 32 48 32 32c44.2 0 80-35.8 80-80V224 192 176c0-35.3 28.7-64 64-64c53 0 96 43 96 96v16c0 35.3-28.7 64-64 64H480zM272 368v64h32V368H272zm-160 0v64h32V368H112zM496 224a16 16 0 1 0 0-32 16 16 0 1 0 0 32z"/></svg>
        </a>
      ` : ''}
        <a class="btn btn-chat-secondary js-chat-translation-btn" data-text="${escapedContent}" data-language="${language}" href="javascript:void(0)">
          <i class="fa fa-language" aria-hidden="true"></i>
        </a>
    </div>
</div>`;
  }

  showAudioButtonIfAvailable(messageId) {
    // Use setTimeout to ensure the DOM has been updated with the new message
    setTimeout(() => {
      const audioKey = `audioUrl_${messageId}`;
      const listenBtn = document.querySelector(`.js-user-chat-listen-yourself-btn[data-chat-message-id="${messageId}"]`);

      if (localStorage.getItem(audioKey) && listenBtn) {
        listenBtn.classList.remove('d-none');
      }
    }, 100);
  }

  disconnect() {
    // Clear heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // Clear refresh timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    if (this.ws) {
      if (this.dedicatedWorker) this.ws.tenantIntentionalClose = true;
      this.ws.close();
      this.ws = null;
    }
  }
}
